import Ellipse from "./icons/Ellipse";
import MarketplaceSVG from "./icons/features/MarketplaceSVG";
import ArchitectureSVG from "./icons/features/ArchitectureSVG";
import NetworkSVG from "./icons/features/NetworkSVG";
import ArbitrationSVG from "./icons/features/ArbitrationSVG";
import SecuritySVG from "./icons/features/SecuritySVG";

interface Feature {
  title: string;
  bulletPoints: {
    title: string;
    description: string;
  }[];
  SVGComponent: React.FC<{ color?: string }>;
  imagePosition: "left" | "right";
}

const features: Feature[] = [
  {
    title: "Trustless Peer-to-Peer Marketplace",
    bulletPoints: [
      {
        title: "No Middlemen Needed",
        description:
          "Connect directly with buyers and sellers without relying on centralized authorities.",
      },
      {
        title: "Security Guaranteed",
        description:
          "LOCAL protocol uses advanced cryptoeconomic models to ensure safe and reliable transactions.",
      },
    ],
    SVGComponent: MarketplaceSVG,
    imagePosition: "right",
  },
  {
    title: "Programmable and Extensible Architecture",
    bulletPoints: [
      {
        title: "Customizable Marketplaces",
        description:
          "Tailor the platform to suit specific markets and unique use cases.",
      },
      {
        title: "Innovative Services",
        description:
          "Enable new types of services and products that were previously not feasible.",
      },
    ],
    SVGComponent: ArchitectureSVG,
    imagePosition: "left",
  },
  {
    title: "Self-Optimizing Network",
    bulletPoints: [
      {
        title: "User-Owned Economy",
        description:
          "Tokens are distributed to participants, rewarding real contributions to the network.",
      },
      {
        title: "Adaptive Incentives",
        description:
          "The system dynamically adjusts to market conditions, aligning incentives naturally.",
      },
    ],
    SVGComponent: NetworkSVG,
    imagePosition: "right",
  },
  {
    title: "Robust Arbitration Protocol",
    bulletPoints: [
      {
        title: "Dispute Resolution",
        description:
          "Handle conflicts efficiently without the need for trusted intermediaries.",
      },
      {
        title: "Service Guarantees",
        description:
          "Both buyers and sellers have assurances, fostering trust in every transaction.",
      },
    ],
    SVGComponent: ArbitrationSVG,
    imagePosition: "left",
  },
  {
    title: "Resistance to Manipulation",
    bulletPoints: [
      {
        title: "Sybil Attack Defense",
        description:
          "LOCAL protocol's unique transaction graph and centrality measures protect against fake transactions and collusion.",
      },
      {
        title: "Dynamic Trust Building",
        description:
          "Participants build reputation through real interactions, enhancing network reliability.",
      },
    ],
    SVGComponent: SecuritySVG,
    imagePosition: "right",
  },
];

const FeaturesSection: React.FC = () => {
  const renderFeatureContent = (feature: Feature, i: number) => (
    <div
      className={` sm:text-lg ${
        i % 2 === 0 ? "text-gray-900" : "text-gray-900"
      }`}
    >
      <h2 className="mb-4 text-2xl md:text-3xl font-semibold tracking-tight">
        {feature.title}
      </h2>
      <ul className="pt-8 space-y-5 my-4">
        {feature.bulletPoints.map((point, index) => (
          <li key={index} className="flex space-x-3">
            <Ellipse fill={"#FFFFFF"} width={20} height={20} />
            <span className="text-base font-medium leading-tight ">
              <strong>{point.title}:</strong> {point.description}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );

  const renderFeatureImage = (feature: Feature, index: number) => (
    <div className="hidden lg:flex rounded-lg bg-white p-8 relative overflow-hidden">
      <div className="absolute inset-0 bg-narrow-stripes opacity-5"></div>
      <feature.SVGComponent color={index % 2 === 0 ? "#2A9D8F" : "#2563EB"} />
    </div>
  );

  return (
    <section className="bg-white">
      <div className="pt-8 lg:pt-24">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-6 lg:text-center">
          <h2 className="mt-2 text-3xl leading-8 font-semibold tracking-tight text-gray-900 sm:text-4xl">
            A Better Way to Build Marketplaces
          </h2>
          <p className="mt-4 max-w-2xl text-xl lg:mx-auto mb-20">
            LOCAL protocol offers cutting-edge features that redefine
            peer-to-peer marketplaces and decentralized networks.
          </p>
        </div>

        {features.map((feature, index) => (
          <div key={index} className="w-full overflow-hidden">
            <div
              className={`relative ${
                index % 2 === 0 ? "gradient-1" : "gradient-2"
              }`}
            >
              <div className="absolute inset-0 bg-white/90"></div>
              <div className="relative max-w-screen-xl mx-auto items-center gap-8 lg:grid lg:grid-cols-2 xl:gap-16 py-12 lg:py-16 px-4 lg:px-6">
                {feature.imagePosition === "left" &&
                  renderFeatureImage(feature, index)}
                {renderFeatureContent(feature, index)}
                {feature.imagePosition === "right" &&
                  renderFeatureImage(feature, index)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default FeaturesSection;
